<template>
	<div class="product-feed">
		<!-- Toolbar -->
		<div class="toolbar d-flex flex-wrap align-items-center justify-content-between mb-3">
			<h2 class="h4 mb-0 feed-heading">Product Feed</h2>
			<div class="d-flex flex-wrap align-items-center gap-2">
				<button class="btn btn-outline-secondary btn-sm" @click="showHelpModal = true" title="Help">
					<i class="fas fa-question-circle"></i>
				</button>
				<button class="btn btn-success btn-sm" @click="bulkApprove" :disabled="selectedAsins.length === 0 || bulkActionInProgress">
					Approve Selected ({{ selectedAsins.length }})
				</button>
				<button class="btn btn-danger btn-sm" @click="bulkReject" :disabled="selectedAsins.length === 0 || bulkActionInProgress">
					Reject Selected ({{ selectedAsins.length }})
				</button>
				<input type="text" class="form-control form-control-sm" placeholder="Filter by ASIN or title..." style="width:200px;" v-model="searchQuery" @input="onSearch" />
				<div class="d-flex align-items-center gap-1">
					<label class="form-label-sm mb-0 me-1" for="perPageSelect">Per page:</label>
					<select id="perPageSelect" class="form-select form-select-sm" style="width:70px;" v-model="perPage" @change="onPerPageChange">
						<option v-for="size in perPageOptions" :key="size" :value="size">{{ size }}</option>
					</select>
				</div>
				<button class="btn btn-primary btn-sm" @click="importApproved" :disabled="importing">
					{{ importing ? 'Importing...' : 'Import Approved' }}
				</button>
				<!-- Price & SKU actions -->
				<button class="btn btn-warning btn-sm" @click="savePriceOverrides">Save Prices</button>
				<button class="btn btn-secondary btn-sm" @click="bulkResetPrices">Reset Prices</button>
				<button class="btn btn-info btn-sm" @click="saveSkuOverrides">Save SKUs</button>
				<button class="btn btn-secondary btn-sm" @click="bulkResetSkus">Reset SKUs</button>
			</div>
		</div>

		<!-- Flash message -->
		<div v-if="flashMessage" class="alert" :class="'alert-' + flashType" role="alert">
			{{ flashMessage }}
		</div>

		<!-- Category Search & Import -->
		<div class="search-import-section mb-3 p-3 bg-light rounded border">
			<div class="row g-2 align-items-end">
				<div class="col-md-3">
					<label class="form-label fw-bold mb-1">Amazon Category</label>
					<select class="form-select" v-model="selectedCategory">
						<option value="">-- Select Category --</option>
						<option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
					</select>
				</div>
				<div class="col-md-5">
					<label class="form-label fw-bold mb-1">Search (title, ASIN, brand...)</label>
					<input type="text" class="form-control" v-model="searchImportTerm" placeholder="e.g., Kodak PIXPRO FZ45" @keyup.enter="runSearchImport" />
				</div>
				<div class="col-md-2">
					<label class="form-label fw-bold mb-1">Pages</label>
					<select class="form-select" v-model="searchPages">
						<option v-for="p in [1,2,3,5]" :key="p" :value="p">{{ p }}</option>
					</select>
				</div>
				<div class="col-md-2">
					<button class="btn btn-primary w-100" @click="runSearchImport" :disabled="searchImporting || !selectedCategory || !searchImportTerm">
						{{ searchImporting ? 'Searching...' : 'Search & Import' }}
					</button>
				</div>
			</div>
			<div v-if="searchImportMessage" class="mt-2 small" :class="searchImportError ? 'text-danger' : 'text-success'">
				{{ searchImportMessage }}
			</div>
		</div>

		<!-- Table Area -->
		<div class="table-area position-relative">
			<!-- Top Pagination -->
			<div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
				<div class="text-muted small">Showing {{ from }} – {{ to }} of {{ total }} products</div>
				<nav>
					<ul class="pagination pagination-sm mb-0">
						<li class="page-item" :class="{ disabled: currentPage === 1 }">
							<a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">«</a>
						</li>
						<li v-for="page in displayedPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
							<a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
						</li>
						<li class="page-item" :class="{ disabled: currentPage === lastPage }">
							<a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">»</a>
						</li>
					</ul>
				</nav>
			</div>

			<!-- Table -->
			<div class="table-responsive" :class="{ 'table-loading': loading || searchImporting }">
				<table class="table table-bordered table-hover table-striped">
					<thead>
						<tr>
							<th style="width:40px;"><input type="checkbox" v-model="selectAll" /></th>
							<th style="width:50px;">#</th>
							<th style="width:70px;">Image</th>
							<th @click="sortBy('asin')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Amazon Standard Identification Number – click to open product page'">
								ASIN <span v-if="sortField === 'asin'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('title')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Product title / name'">
								Title <span v-if="sortField === 'title'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('brand')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Brand / manufacturer'">
								Brand <span v-if="sortField === 'brand'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('mpn')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Model / Part Number (MPN)'">
								Model <span v-if="sortField === 'mpn'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th style="width:220px;">Description</th>
							<th>Your Price</th>
							<th>SKU</th>
							<!-- Three price columns -->
							<th @click="sortBy('price_highest')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Highest listed price on Amazon (including shipping)'">
								Highest Price <span v-if="sortField === 'price_highest'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('price_buybox')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Current Buy Box price on Amazon'">
								Buy Box Price <span v-if="sortField === 'price_buybox'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('price_lowest')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Lowest listed price on Amazon (including shipping)'">
								Lowest Price <span v-if="sortField === 'price_lowest'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<!-- Existing profit columns -->
							<th @click="sortBy('estimated_profit')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Estimated profit = sale price − cost'">
								Est. Profit <span v-if="sortField === 'estimated_profit'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('profit_margin_percent')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Profit margin %'">
								Margin % <span v-if="sortField === 'profit_margin_percent'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('profit_score')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Profit score (0–100)'">
								Profit Score <span v-if="sortField === 'profit_score'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('competition_score')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Competition score (0–100)'">
								Competition <span v-if="sortField === 'competition_score'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('risk_score')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Risk score (0–100)'">
								Risk <span v-if="sortField === 'risk_score'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('decision')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Recommended action'">
								Decision <span v-if="sortField === 'decision'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
							<th @click="sortBy('product_tier')" style="cursor:pointer;" class="has-tooltip" :data-tooltip="'Product tier S/A/B/C/D'">
								Tier <span v-if="sortField === 'product_tier'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(p, index) in products" :key="p.asin" :class="{ 'table-warning': isChanged(p) }">
							<td><input type="checkbox" v-model="selectedAsins" :value="p.asin" /></td>
							<td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
							<td>
								<img
									v-if="firstImage(p.images)"
									:src="firstImage(p.images)"
									alt="Product image"
									class="img-thumbnail product-thumb clickable-image"
									@error="onImageError(p)"
									@click="openImageGallery(p)"
								/>
								<span v-else class="text-muted">No image</span>
							</td>
							<td style="white-space:nowrap;">
								<a :href="'https://www.amazon.com/dp/' + p.asin" target="_blank" rel="noopener noreferrer">
									<code>{{ p.asin }}</code>
								</a>
							</td>
							<td>{{ truncate(p.title, 60) }}</td>
							<td>{{ p.brand || '-' }}</td>
							<td>{{ p.mpn || '-' }}</td>
							<td>
								<span class="description-truncate" @click="openProductDetail(p)">
									{{ truncate(p.description, 80) || 'No description' }}
								</span>
							</td>
							<td>
								<input
									type="number"
									step="0.01"
									class="form-control form-control-sm"
									v-model="p.custom_sale_price"
									:placeholder="formatPrice(p.price_highest)"
									style="width:100px;"
								/>
								<small class="text-muted">(highest: ${{ formatPrice(p.price_highest) }})</small>
							</td>
							<td>
								<div v-if="!p.mpn" class="text-warning small" style="white-space:nowrap;">
									<i class="fas fa-exclamation-triangle"></i> No MPN
								</div>
								<input
									type="text"
									class="form-control form-control-sm"
									v-model="p.custom_sku"
									:placeholder="getDefaultSku(p) || 'No MPN'"
									style="width:150px;"
								/>
								<small class="text-muted">(default: {{ getDefaultSku(p) || 'N/A' }})</small>
							</td>
							<!-- Three price columns -->
							<td>${{ formatPrice(p.price_highest) }}</td>
							<td>${{ formatPrice(p.price_buybox) }}</td>
							<td>${{ formatPrice(p.price_lowest) }}</td>
							<!-- Existing profit columns -->
							<td>${{ formatPrice(p.estimated_profit) }}</td>
							<td>{{ p.profit_margin_percent || 0 }}%</td>
							<td>{{ p.profit_score || '-' }}</td>
							<td>{{ p.competition_score || '-' }}</td>
							<td>{{ p.risk_score || '-' }}</td>
							<td>
								<span :class="'badge ' + decisionClass(p.decision)">{{ p.decision || 'PENDING' }}</span>
							</td>
							<td>
								<span :class="'badge ' + tierClass(p.product_tier)" :title="tierDescription(p.product_tier)">
									{{ p.product_tier || 'N/A' }}
								</span>
							</td>
						</tr>
						<tr v-if="products.length === 0 && !loading && !searchImporting">
							<td colspan="19" class="text-center">No products found.</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Bottom Pagination -->
			<div class="d-flex flex-wrap align-items-center justify-content-between mt-2">
				<div class="text-muted small">Showing {{ from }} – {{ to }} of {{ total }} products</div>
				<nav>
					<ul class="pagination pagination-sm mb-0">
						<li class="page-item" :class="{ disabled: currentPage === 1 }">
							<a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">«</a>
						</li>
						<li v-for="page in displayedPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
							<a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
						</li>
						<li class="page-item" :class="{ disabled: currentPage === lastPage }">
							<a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">»</a>
						</li>
					</ul>
				</nav>
			</div>

			<!-- Loading Overlay -->
			<div v-if="loading || searchImporting || bulkActionInProgress" class="loading-overlay">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>

		<!-- HELP MODAL -->
		<div v-if="showHelpModal" class="modal-backdrop" @click.self="showHelpModal = false">
			<div class="modal-dialog modal-lg modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title"><i class="fas fa-info-circle text-primary"></i> Product Feed – Field Explanations</h5>
						<button type="button" class="btn-close" @click="showHelpModal = false"></button>
					</div>
					<div class="modal-body">
						<p>This table displays Amazon products fetched via the <strong>profit engine</strong>. Each product is scored and classified to help you decide which items to list.</p>
						<h6 class="mt-3">📋 Column Descriptions</h6>
						<table class="table table-sm table-bordered">
							<thead><tr><th>Column</th><th>Description</th></tr></thead>
							<tbody>
								<tr><td><strong>#</strong></td><td>Row number on current page.</td></tr>
								<tr><td><strong>Image</strong></td><td>Main product image from Amazon.</td></tr>
								<tr><td><strong>ASIN</strong></td><td>Amazon Standard Identification Number – click to open the product page on Amazon.</td></tr>
								<tr><td><strong>Title</strong></td><td>Product title from Amazon.</td></tr>
								<tr><td><strong>Brand</strong></td><td>Brand or manufacturer name.</td></tr>
								<tr><td><strong>Model</strong></td><td>Model / Part Number (MPN) if available.</td></tr>
								<tr><td><strong>Description</strong></td><td>Product description (truncated; click to see full text).</td></tr>
								<tr><td><strong>Your Price</strong></td><td>Override the sale price for this product (leave blank to use the highest Amazon price).</td></tr>
								<tr><td><strong>SKU</strong></td><td>Your SKU for this product. Default = sanitized MPN + "-UVG". Override as needed.</td></tr>
								<tr><td><strong>Highest Price</strong></td><td>The highest total price (listing + shipping) currently offered on Amazon.</td></tr>
								<tr><td><strong>Buy Box Price</strong></td><td>Current price shown in the Amazon Buy Box.</td></tr>
								<tr><td><strong>Lowest Price</strong></td><td>The lowest total price (listing + shipping) currently offered on Amazon.</td></tr>
								<tr><td><strong>Est. Profit</strong></td><td>Estimated profit = <em>estimated sale price</em> – <em>estimated cost</em>.</td></tr>
								<tr><td><strong>Margin %</strong></td><td>Estimated profit as a percentage of sale price.</td></tr>
								<tr><td><strong>Profit Score</strong></td><td>Score (0–100) indicating profit potential. Higher = better.</td></tr>
								<tr><td><strong>Competition</strong></td><td>Score (0–100) based on number of sellers and price competition. Lower = less competition.</td></tr>
								<tr><td><strong>Risk</strong></td><td>Score (0–100) based on product uncertainty, seasonality, category volatility. Lower = less risk.</td></tr>
								<tr><td><strong>Decision</strong></td><td>Recommended action. See colour codes below.</td></tr>
								<tr><td><strong>Tier</strong></td><td>Overall product quality grade based on combined scores. See colour codes below.</td></tr>
							</tbody>
						</table>
						<h6 class="mt-3">🎨 Decision Colours</h6>
						<div class="d-flex flex-wrap gap-2 mb-2">
							<span class="badge badge-success">APPROVED</span> – Approved for import (ready to move to your catalogue).
							<span class="badge badge-danger">REJECTED</span> – Rejected (not to be imported).
							<span class="badge badge-warning">WATCH</span> – Monitor but don't import yet.
							<span class="badge badge-secondary">IGNORE</span> – Not worth pursuing.
							<span class="badge badge-primary">BUY</span> – Strong buy recommendation.
						</div>
						<h6 class="mt-3">🏆 Tier Colours & Meaning</h6>
						<div class="d-flex flex-wrap gap-2 mb-2">
							<span class="badge badge-success">S</span> – <strong>Best</strong>: high profit, low risk, low competition – top opportunity.
							<span class="badge badge-primary">A</span> – <strong>Good</strong>: solid opportunity with good returns.
							<span class="badge badge-info">B</span> – <strong>Average</strong>: moderate potential, acceptable.
							<span class="badge badge-warning">C</span> – <strong>Below average</strong>: higher risk or lower margins, caution advised.
							<span class="badge badge-secondary">D</span> – <strong>Poor</strong>: likely not profitable, avoid.
						</div>
						<h6 class="mt-3">⚙️ How Scores are Calculated</h6>
						<p>The profit engine uses a combination of:</p>
						<ul>
							<li><strong>Profit Score</strong> – based on estimated profit and margin.</li>
							<li><strong>Competition Score</strong> – based on number of sellers and price spread.</li>
							<li><strong>Risk Score</strong> – based on category volatility, sales rank, and other signals.</li>
						</ul>
						<p>These are weighted and normalized to produce the final <strong>Tier</strong> and <strong>Decision</strong>.</p>
						<p class="text-muted small mt-2"><i class="fas fa-info-circle"></i> This scoring is designed to help you quickly evaluate products. You can override decisions manually using the Approve/Reject buttons.</p>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" @click="showHelpModal = false">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- PRODUCT DETAIL MODAL -->
		<div v-if="showProductDetailModal" class="modal-backdrop" @click.self="closeProductDetail">
			<div class="modal-dialog modal-xl modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="fas fa-box text-primary"></i> Product Details
							<small class="text-muted ms-2" style="font-size:0.7rem;">{{ selectedProduct ? selectedProduct.asin : '' }}</small>
						</h5>
						<button type="button" class="btn-close" @click="closeProductDetail"></button>
					</div>
					<div class="modal-body" v-if="selectedProduct">
						<div class="row">
							<div class="col-md-4">
								<div style="position:relative; background:#f8f9fa; border-radius:8px; padding:10px; text-align:center; min-height:200px;">
									<img
										v-if="detailImages.length"
										:src="detailImages[detailImageIndex]"
										alt="Product image"
										style="max-width:100%; max-height:300px; object-fit:contain;"
									/>
									<div v-else class="text-muted">No images</div>
									<div v-if="detailImages.length > 1" style="margin-top:10px;">
										<button class="btn btn-sm btn-outline-secondary" @click="prevDetailImage">‹</button>
										<span class="mx-2">{{ detailImageIndex + 1 }} / {{ detailImages.length }}</span>
										<button class="btn btn-sm btn-outline-secondary" @click="nextDetailImage">›</button>
									</div>
								</div>
							</div>
							<div class="col-md-8">
								<h5>{{ selectedProduct.title }}</h5>
								<table class="table table-sm table-bordered mt-2">
									<tbody>
										<tr><td><strong>ASIN</strong></td><td>{{ selectedProduct.asin }}</td></tr>
										<tr><td><strong>Brand</strong></td><td>{{ selectedProduct.brand || '-' }}</td></tr>
										<tr><td><strong>Model / MPN</strong></td><td>{{ selectedProduct.mpn || '-' }}</td></tr>
										<tr><td><strong>Your SKU</strong></td><td>{{ selectedProduct.custom_sku || getDefaultSku(selectedProduct) || 'N/A' }}</td></tr>
										<tr><td><strong>Buy Box Price</strong></td><td>${{ formatPrice(selectedProduct.price_buybox) }}</td></tr>
										<tr><td><strong>Est. Profit</strong></td><td>${{ formatPrice(selectedProduct.estimated_profit) }}</td></tr>
										<tr><td><strong>Margin %</strong></td><td>{{ selectedProduct.profit_margin_percent || 0 }}%</td></tr>
										<tr><td><strong>Profit Score</strong></td><td>{{ selectedProduct.profit_score || '-' }}</td></tr>
										<tr><td><strong>Competition</strong></td><td>{{ selectedProduct.competition_score || '-' }}</td></tr>
										<tr><td><strong>Risk</strong></td><td>{{ selectedProduct.risk_score || '-' }}</td></tr>
										<tr><td><strong>Decision</strong></td><td><span :class="'badge ' + decisionClass(selectedProduct.decision)">{{ selectedProduct.decision || 'PENDING' }}</span></td></tr>
										<tr><td><strong>Tier</strong></td><td><span :class="'badge ' + tierClass(selectedProduct.product_tier)">{{ selectedProduct.product_tier || 'N/A' }}</span></td></tr>
									</tbody>
								</table>
								<h6 class="mt-3">Description</h6>
								<div class="description-full">{{ selectedProduct.description || 'No description available.' }}</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" @click="closeProductDetail">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- IMAGE GALLERY MODAL (full‑screen with left/right arrows and visible close button) -->
		<div v-if="showImageModal" class="modal-backdrop" @click.self="closeImageGallery">
			<div class="modal-dialog modal-lg modal-dialog-centered" style="max-width:95%;">
				<div class="modal-content" style="background:transparent; border:none; box-shadow:none;">
					<div class="modal-body" style="padding:0; position:relative; background:rgba(0,0,0,0.9); border-radius:12px; min-height:60vh; display:flex; align-items:center; justify-content:center;">
						<!-- Close button (top‑right, large, white) -->
						<button
							type="button"
							class="btn-close btn-close-white"
							style="position:absolute; top:15px; right:20px; z-index:10; font-size:2rem; opacity:0.9; filter:drop-shadow(0 2px 8px rgba(0,0,0,0.8));"
							@click="closeImageGallery"
						></button>

						<!-- Left arrow (absolute left) -->
						<button
							v-if="galleryImages.length > 1"
							class="btn btn-light btn-lg rounded-circle"
							style="position:absolute; left:15px; top:50%; transform:translateY(-50%); opacity:0.7; z-index:5; font-size:2rem; padding:0.25rem 0.75rem;"
							@click="prevImage"
						>‹</button>

						<!-- Right arrow (absolute right) -->
						<button
							v-if="galleryImages.length > 1"
							class="btn btn-light btn-lg rounded-circle"
							style="position:absolute; right:15px; top:50%; transform:translateY(-50%); opacity:0.7; z-index:5; font-size:2rem; padding:0.25rem 0.75rem;"
							@click="nextImage"
						>›</button>

						<!-- Image -->
						<div style="display:flex; align-items:center; justify-content:center; width:100%; padding:20px;">
							<img
								v-if="galleryImages.length"
								:src="galleryImages[galleryIndex]"
								alt="Product image"
								style="max-width:100%; max-height:80vh; object-fit:contain; border-radius:8px;"
							/>
							<div v-else class="text-white">No images available</div>
						</div>

						<!-- Image counter (bottom‑center) -->
						<div
							v-if="galleryImages.length > 1"
							class="text-white"
							style="position:absolute; bottom:15px; left:0; right:0; text-align:center; z-index:5; font-size:0.9rem; opacity:0.7;"
						>
							{{ galleryIndex + 1 }} / {{ galleryImages.length }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	data() {
		return {
			products: [],
			loading: false,
			importing: false,
			currentPage: 1,
			lastPage: 1,
			total: 0,
			perPage: 50,
			perPageOptions: [10, 25, 50, 100],
			searchQuery: '',
			sortField: 'id',
			sortDirection: 'desc',
			imageErrors: new Set(),
			selectedAsins: [],
			selectAll: false,
			bulkActionInProgress: false,
			selectedCategory: '',
			searchImportTerm: '',
			searchPages: 1,
			searchImporting: false,
			searchImportMessage: '',
			searchImportError: false,
			showHelpModal: false,

			// Product Detail Modal
			showProductDetailModal: false,
			selectedProduct: null,
			detailImages: [],
			detailImageIndex: 0,

			// Image Gallery Modal
			showImageModal: false,
			galleryImages: [],
			galleryIndex: 0,

			// Flash message
			flashMessage: '',
			flashType: 'success',

			categories: [
				{id: '330405011', name: '📷 Point & Shoot Digital Cameras'},
				{id: '281052', name: '📷 Digital Cameras'},
				{id: '172282', name: '🔭 Camera Lenses'},
				{id: '3348091', name: '🎒 Camera Accessories'},
				{id: '2335752011', name: '📷 DSLR Cameras'},
				{id: '14212751', name: '📷 Mirrorless Cameras'},
				{id: '281407', name: '🎒 Camera Bags & Cases'},
				{id: '3017927011', name: '🔭 Tripods & Monopods'},
				{id: '3348081', name: '💾 Memory Cards'},
				{id: '281405', name: '📷 Camcorders'},
				{id: '3348082', name: '🔭 Binoculars & Scopes'},
				{id: '281412', name: '🔦 Lighting & Studio'},
				{id: '1063304', name: '📷 Photo Printers'},
				{id: '3017942011', name: '🔌 Camera Batteries & Chargers'},
				{id: '172605', name: '🖼️ Photo Albums & Frames'},
			],
		};
	},

	computed: {
		from() {
			return (this.currentPage - 1) * this.perPage + 1;
		},
		to() {
			return Math.min(this.currentPage * this.perPage, this.total);
		},
		displayedPages() {
			const pages = [];
			const maxVisible = 5;
			let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
			const end = Math.min(this.lastPage, start + maxVisible - 1);
			if (end - start < maxVisible - 1) {
				start = Math.max(1, end - maxVisible + 1);
			}
			for (let i = start; i <= end; i++) {
				pages.push(i);
			}
			return pages;
		},
		allAsins() {
			return this.products.map((p) => p.asin);
		},
	},

	watch: {
		selectAll(value) {
			this.selectedAsins = value ? this.allAsins : [];
		},
		selectedAsins(value) {
			if (value.length === this.allAsins.length && this.allAsins.length > 0) {
				this.selectAll = true;
			} else {
				this.selectAll = false;
			}
		},
	},

	methods: {
		// ---- Default SKU generator ----
		getDefaultSku(p) {
			if (p.mpn) {
				const clean = p.mpn.replace(/[^a-zA-Z0-9]/g, '');
				return clean ? clean + '-UVG' : null;
			}
			return null;
		},

		// ---- Change detection ----
		isChanged(p) {
			return (p.custom_sale_price !== p.originalCustomPrice) ||
			       (p.custom_sku !== p.originalCustomSku);
		},

		load() {
			this.loading = true;
			this.$axios
				.get('/products/feed', {
					params: {
						page: this.currentPage,
						per_page: this.perPage,
						search: this.searchQuery || undefined,
						sort: this.sortField,
						direction: this.sortDirection,
					},
				})
				.then((res) => {
					this.products = res.data.data;
					this.currentPage = res.data.current_page;
					this.lastPage = res.data.last_page;
					this.total = res.data.total;
					this.perPage = res.data.per_page;
					// Store original values for change detection
					this.products.forEach(p => {
						p.originalCustomPrice = p.custom_sale_price;
						p.originalCustomSku = p.custom_sku;
					});
				})
				.catch((err) => {
					console.error('Feed error:', err);
					alert('Failed to load products.');
				})
				.finally(() => {
					this.loading = false;
				});
		},

		changePage(page) {
			if (page < 1 || page > this.lastPage || page === this.currentPage) return;
			this.currentPage = page;
			this.selectedAsins = [];
			this.selectAll = false;
			this.load();
		},

		onPerPageChange() {
			this.currentPage = 1;
			this.selectedAsins = [];
			this.selectAll = false;
			this.load();
		},

		onSearch() {
			this.currentPage = 1;
			this.selectedAsins = [];
			this.selectAll = false;
			this.load();
		},

		sortBy(field) {
			if (this.sortField === field) {
				this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
			} else {
				this.sortField = field;
				this.sortDirection = 'asc';
			}
			this.currentPage = 1;
			this.selectedAsins = [];
			this.selectAll = false;
			this.load();
		},

		// ---- Price Overrides ----
		async savePriceOverrides() {
			const updates = this.products.map(p => ({
				asin: p.asin,
				custom_sale_price: p.custom_sale_price
			}));
			try {
				await this.$axios.post('/products/feed/update-price', { updates });
				this.products.forEach(p => {
					p.originalCustomPrice = p.custom_sale_price;
				});
				this.flashMessage = 'Prices updated successfully.';
				this.flashType = 'success';
				setTimeout(() => { this.flashMessage = ''; }, 3000);
			} catch (error) {
				console.error('Failed to save overrides:', error);
				this.flashMessage = 'Failed to save prices.';
				this.flashType = 'danger';
				setTimeout(() => { this.flashMessage = ''; }, 5000);
			}
		},

		async bulkResetPrices() {
			if (!confirm('Reset all custom prices on this page to default (highest price)?')) return;
			this.products.forEach(p => {
				p.custom_sale_price = null;
			});
			await this.savePriceOverrides();
		},

		// ---- SKU Overrides ----
		async saveSkuOverrides() {
			const updates = this.products.map(p => ({
				asin: p.asin,
				custom_sku: p.custom_sku
			}));
			try {
				await this.$axios.post('/products/feed/update-sku', { updates });
				this.products.forEach(p => {
					p.originalCustomSku = p.custom_sku;
				});
				this.flashMessage = 'SKUs updated successfully.';
				this.flashType = 'success';
				setTimeout(() => { this.flashMessage = ''; }, 3000);
			} catch (error) {
				console.error('Failed to save SKUs:', error);
				this.flashMessage = 'Failed to save SKUs.';
				this.flashType = 'danger';
				setTimeout(() => { this.flashMessage = ''; }, 5000);
			}
		},

		async bulkResetSkus() {
			if (!confirm('Reset all custom SKUs to default?')) return;
			this.products.forEach(p => {
				p.custom_sku = null;
			});
			await this.saveSkuOverrides();
		},

		// ---- Bulk Actions ----
		async bulkApprove() {
			await this.bulkUpdateDecision('APPROVED');
		},
		async bulkReject() {
			await this.bulkUpdateDecision('REJECTED');
		},
		async bulkUpdateDecision(decision) {
			if (this.selectedAsins.length === 0) {
				alert('Please select at least one product.');
				return;
			}
			if (!confirm(`Are you sure you want to ${decision} ${this.selectedAsins.length} product(s)?`)) return;

			this.bulkActionInProgress = true;
			try {
				const response = await this.$axios.post('/products/feed/bulk-decision', {
					asins: this.selectedAsins,
					decision: decision,
				});
				alert(response.data.message);
				this.selectedAsins = [];
				this.selectAll = false;
				this.load();
			} catch (error) {
				console.error('Bulk update error:', error);
				let msg = 'Failed to update products.';
				if (error.response) {
					const data = error.response.data;
					msg = data && data.message ? data.message : `Server error (${error.response.status})`;
				} else if (error.request) {
					msg = 'No response from server. Check network.';
				} else {
					msg = error.message;
				}
				alert(msg);
			} finally {
				this.bulkActionInProgress = false;
			}
		},

		// ---- Category Search & Import ----
		async runSearchImport() {
			if (!this.selectedCategory || !this.searchImportTerm.trim()) {
				this.searchImportMessage = 'Please select a category and enter a search term.';
				this.searchImportError = true;
				return;
			}

			this.searchImporting = true;
			this.searchImportMessage = '';
			this.searchImportError = false;

			try {
				const response = await this.$axios.post('/products/search-import', {
					keyword: this.searchImportTerm.trim(),
					category_id: this.selectedCategory,
					pages: this.searchPages,
				});

				console.log('Import response:', response);

				if (response.status === 200) {
					this.searchImportMessage = response.data.message || 'Import started.';
					this.searchImportError = false;
					setTimeout(() => {
						this.currentPage = 1;
						this.load();
					}, 3000);
				} else {
					throw new Error(`Unexpected status: ${response.status}`);
				}
			} catch (error) {
				console.error('Search import error:', error);
				let msg = 'Search & import failed.';
				if (error.response) {
					console.error('Response data:', error.response.data);
					console.error('Response status:', error.response.status);
					if (error.response.data && error.response.data.message) {
						msg = error.response.data.message;
					} else {
						msg = `Server error (${error.response.status})`;
					}
				} else if (error.request) {
					msg = 'No response from server. Check network.';
				} else {
					msg = error.message;
				}
				this.searchImportMessage = msg;
				this.searchImportError = true;
			} finally {
				this.searchImporting = false;
			}
		},

		// ---- Single decision ----
		approve(product) {
			this.updateDecision(product, 'APPROVED');
		},
		reject(product) {
			this.updateDecision(product, 'REJECTED');
		},
		updateDecision(product, decision) {
			this.$axios
				.post(`/products/feed/${product.asin}/decision`, { decision })
				.then(() => {
					product.decision = decision;
				})
				.catch((err) => {
					console.error('Decision update failed:', err);
					alert('Failed to update decision.');
				});
		},

		// ---- Import Approved ----
		importApproved() {
			if (!confirm('Import all approved products?')) return;
			this.importing = true;
			this.$axios
				.post('/products/feed/import')
				.then((res) => {
					alert(res.data.message);
					this.load();
				})
				.catch((err) => {
					console.error('Import error:', err);
					alert('Import failed.');
				})
				.finally(() => {
					this.importing = false;
				});
		},

		// ---- Image helpers ----
		firstImage(imagesJson) {
			if (!imagesJson) return null;
			try {
				const images = typeof imagesJson === 'string' ? JSON.parse(imagesJson) : imagesJson;
				if (Array.isArray(images) && images.length > 0) {
					if (typeof images[0] === 'string') return images[0];
					if (typeof images[0] === 'object' && images[0].link) return images[0].link;
				}
				return null;
			} catch (e) {
				return null;
			}
		},
		onImageError(product) {
			product.images = null;
		},

		// ---- Product Detail Modal ----
		openProductDetail(product) {
			this.selectedProduct = product;
			if (product.images) {
				try {
					let imgs = typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
					if (Array.isArray(imgs)) {
						this.detailImages = imgs.map((img) => {
							if (typeof img === 'string') return img;
							if (img.link) return img.link;
							return img;
						});
					} else {
						this.detailImages = [];
					}
				} catch (e) {
					this.detailImages = [];
				}
			} else {
				this.detailImages = [];
			}
			this.detailImageIndex = 0;
			this.showProductDetailModal = true;
		},
		closeProductDetail() {
			this.showProductDetailModal = false;
			this.selectedProduct = null;
			this.detailImages = [];
			this.detailImageIndex = 0;
		},
		prevDetailImage() {
			if (this.detailImages.length > 1) {
				this.detailImageIndex = (this.detailImageIndex - 1 + this.detailImages.length) % this.detailImages.length;
			}
		},
		nextDetailImage() {
			if (this.detailImages.length > 1) {
				this.detailImageIndex = (this.detailImageIndex + 1) % this.detailImages.length;
			}
		},

		// ---- Image Gallery Modal ----
		openImageGallery(product) {
			if (!product.images) return;
			try {
				let images = typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
				if (Array.isArray(images) && images.length > 0) {
					this.galleryImages = images.map((img) => {
						if (typeof img === 'string') return img;
						if (img.link) return img.link;
						return img;
					});
					this.galleryIndex = 0;
					this.showImageModal = true;
				}
			} catch (e) {
				console.warn('Could not parse images:', e);
			}
		},
		closeImageGallery() {
			this.showImageModal = false;
			this.galleryImages = [];
			this.galleryIndex = 0;
		},
		prevImage() {
			if (this.galleryImages.length > 1) {
				this.galleryIndex = (this.galleryIndex - 1 + this.galleryImages.length) % this.galleryImages.length;
			}
		},
		nextImage() {
			if (this.galleryImages.length > 1) {
				this.galleryIndex = (this.galleryIndex + 1) % this.galleryImages.length;
			}
		},

		// ---- Formatting ----
		truncate(text, len) {
			if (!text) return '';
			return text.length > len ? text.slice(0, len) + '…' : text;
		},
		formatPrice(val) {
			if (val == null) return '0.00';
			return Number(val).toFixed(2);
		},

		// ---- Badge classes ----
		tierClass(tier) {
			const map = { S: 'badge-success', A: 'badge-primary', B: 'badge-info', C: 'badge-warning', D: 'badge-secondary' };
			return map[tier] || 'badge-dark';
		},
		tierDescription(tier) {
			const map = {
				S: 'Best quality – high profit, low risk',
				A: 'Good – solid opportunity',
				B: 'Average – moderate potential',
				C: 'Below average – higher risk',
				D: 'Poor – not recommended',
			};
			return map[tier] || 'No tier assigned';
		},
		decisionClass(decision) {
			const map = { APPROVED: 'badge-success', REJECTED: 'badge-danger', WATCH: 'badge-warning', IGNORE: 'badge-secondary', BUY: 'badge-primary' };
			return map[decision] || 'badge-secondary';
		},
	},

	mounted() {
		this.load();
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				if (this.showImageModal) this.closeImageGallery();
				if (this.showProductDetailModal) this.closeProductDetail();
				if (this.showHelpModal) this.showHelpModal = false;
			}
		});
	},
};
</script>

<style scoped>
.feed-heading {
	margin-left: 50px !important;
}
.product-feed {
	font-family: Verdana, Geneva, sans-serif;
	padding: 1rem;
}
.gap-2 {
	gap: 0.5rem;
}
.product-thumb {
	max-width: 50px;
	max-height: 50px;
	object-fit: cover;
	border: 1px solid #ddd;
	border-radius: 4px;
	padding: 2px;
}
.form-select-sm {
	padding-top: 0.2rem;
	padding-bottom: 0.2rem;
}
.table td {
	font-family: Verdana, Geneva, sans-serif;
	vertical-align: middle;
	padding: 8px 6px;
	font-size: 0.8rem;
}
.pagination {
	margin-bottom: 0;
}
.table thead tr th {
	font-weight: 700;
	letter-spacing: normal;
	padding: 10px 4px;
	font-size: 0.7rem;
	color: #101480bf;
	background-color: #f4f8fb !important;
	border-bottom: 2px solid #dee2e6;
	text-transform: uppercase;
}
.table thead th[style*='cursor: pointer']:hover {
	background-color: #e9ecef !important;
}
.table-area {
	position: relative;
}
.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
	border-radius: 4px;
}
.table-loading {
	opacity: 0.5;
	pointer-events: none;
}
.search-import-section {
	border-color: #dee2e6 !important;
}

/* FANCY TOOLTIPS */
.has-tooltip {
	position: relative;
	cursor: help;
}
.has-tooltip::after {
	content: attr(data-tooltip);
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(-8px);
	background: #1e293b;
	color: #ffffff;
	padding: 8px 14px;
	border-radius: 8px;
	font-size: 0.75rem;
	font-weight: 400;
	text-transform: none;
	white-space: nowrap;
	max-width: 300px;
	white-space: normal;
	word-wrap: break-word;
	box-shadow: 0 8px 20px rgba(0,0,0,0.2);
	z-index: 1000;
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.2s ease, transform 0.2s ease;
	font-family: Verdana, Geneva, sans-serif;
	letter-spacing: normal;
	line-height: 1.4;
}
.has-tooltip::before {
	content: '';
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(0);
	border: 6px solid transparent;
	border-top-color: #1e293b;
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.2s ease;
}
.has-tooltip:hover::after,
.has-tooltip:hover::before {
	opacity: 1;
}
.has-tooltip:hover::after {
	transform: translateX(-50%) translateY(-6px);
}
.has-tooltip:hover::before {
	transform: translateX(-50%) translateY(2px);
}

/* Description truncate */
.description-truncate {
	cursor: pointer;
	color: #0d6efd;
	font-size: 0.7rem;
	line-height: 1.3;
	display: block;
	max-height: 3.2em;
	overflow: hidden;
	word-break: break-word;
}
.description-truncate:hover {
	text-decoration: underline;
}
.description-full {
	background: #f8f9fa;
	padding: 1rem;
	border-radius: 8px;
	font-size: 0.9rem;
	line-height: 1.6;
	white-space: pre-wrap;
	word-break: break-word;
}

/* MODAL BACKDROP */
.modal-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0,0,0,0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1050;
	backdrop-filter: blur(4px);
}
.modal-dialog {
	width: 90%;
	max-width: 800px;
	max-height: 90vh;
	overflow-y: auto;
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-dialog.modal-xl {
	max-width: 1140px;
}
.modal-header,
.modal-footer {
	padding: 1rem 1.5rem;
	border-bottom: 1px solid #dee2e6;
}
.modal-footer {
	border-bottom: none;
	border-top: 1px solid #dee2e6;
}
.modal-body {
	padding: 1.5rem;
	overflow-y: auto;
}
.btn-close {
	background: transparent;
	border: none;
	font-size: 1.5rem;
	cursor: pointer;
	opacity: 0.6;
}
.btn-close:hover {
	opacity: 1;
}

.clickable-image {
	cursor: pointer;
}
.clickable-image:hover {
	opacity: 0.8;
}
</style>